import { PanelBase } from "./PanelBase";
import type { GameContext } from "../../GameContext";

interface InventoryItem {
  itemId: string;
  name: string;
  quantity: number;
  iconKey: string;
  equippable: boolean;
}

/**
 * Per M05 (inventory-worker): GET/POST /api/v1/characters/:id/inventory,
 * use/discard. Gateway-routed at /api/v1/characters/:id/inventory* (2.2).
 */
export class InventoryGrid extends PanelBase {
  constructor(root: HTMLElement, ctx: GameContext, private readonly characterId?: string) {
    super(root, ctx, "inventory-grid");
  }

  protected async refresh(): Promise<void> {
    const characterId = this.ctx.selectedCharacterId ?? this.characterId;
    if (!characterId) return;

    this.el.innerHTML = "<p>Loading inventory…</p>";
    const result = await this.ctx.network.rest.get<InventoryItem[]>(
      `/api/v1/characters/${characterId}/inventory`
    );

    if (!result.ok) {
      this.el.innerHTML = `<p class="panel-error">${escapeHtml(result.message)}</p>`;
      return;
    }

    this.render(result.data, characterId);
  }

  private render(items: InventoryItem[], characterId: string): void {
    this.el.innerHTML = "<h3>Inventory</h3>";
    const grid = document.createElement("div");
    grid.className = "inventory-cells";

    if (items.length === 0) {
      const empty = document.createElement("p");
      empty.textContent = "Empty.";
      this.el.appendChild(empty);
      return;
    }

    for (const item of items) {
      const cell = document.createElement("div");
      cell.className = "inventory-cell";
      cell.innerHTML = `
        <div class="item-icon" data-icon="${escapeHtml(item.iconKey)}"></div>
        <div class="item-name">${escapeHtml(item.name)}</div>
        <div class="item-qty">x${item.quantity}</div>
      `;

      if (item.equippable) {
        const equipBtn = document.createElement("button");
        equipBtn.textContent = "Equip";
        equipBtn.addEventListener("click", () => void this.equipItem(characterId, item.itemId));
        cell.appendChild(equipBtn);
      }

      const discardBtn = document.createElement("button");
      discardBtn.textContent = "Discard";
      discardBtn.addEventListener("click", () => void this.discardItem(characterId, item.itemId));
      cell.appendChild(discardBtn);

      grid.appendChild(cell);
    }
    this.el.appendChild(grid);
  }

  private async equipItem(characterId: string, itemId: string): Promise<void> {
    const result = await this.ctx.network.rest.post(`/api/v1/characters/${characterId}/equipment`, { itemId });
    if (result.ok) await this.refresh();
  }

  private async discardItem(characterId: string, itemId: string): Promise<void> {
    const result = await this.ctx.network.rest.post(
      `/api/v1/characters/${characterId}/inventory/${itemId}/discard`,
      {}
    );
    if (result.ok) await this.refresh();
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
